import { useEffect, useRef } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import billingCopy from '@/i18n/ns/billing';
import operationCopy from '@/i18n/ns/usage-operations';
import { useIntlLocale } from '@/workspace/intl-locale';
import { CREDIT_MICROS, type BillingStatus } from './api';
import { useBilling } from './context';
import { credits, shortDate } from './plan-math';

export const LIMIT_NOTICE_SHARE = 0.8;

/** Rollover and support grants remain part of the actual shared balance. */
export function companyCreditNotice(status: BillingStatus | null | undefined) {
  if (!status?.enabled || !status.account.planActive || !status.canAsk) return null;
  const plan =
    status.plans.find((entry) => entry.id === status.account.planId) ?? status.assignedPlan;
  const balance = status.account.creditBalanceMicros;
  if (!plan || balance <= 0 || balance > plan.credits * CREDIT_MICROS * 0.2) return null;
  return balance;
}

/** The in-app notice once a person has used 80% of their own monthly limit (D74). */
export function limitNoticeValues(status: BillingStatus | null | undefined) {
  const limit = status?.memberLimit;
  if (!limit || limit.limitMicros <= 0) return null;
  if (limit.usedMicros < limit.limitMicros * LIMIT_NOTICE_SHARE) return null;
  // At 100% the paused dialog explains the limit instead.
  if (limit.usedMicros >= limit.limitMicros) return null;
  return {
    leftMicros: limit.limitMicros - limit.usedMicros,
    limitMicros: limit.limitMicros,
    resetsAt: status?.account.periodEnd ?? null,
  };
}

/**
 * Re-reads billing once a turn settles, so the notice appears (or the limit
 * dialog takes over) right after the turn that crossed 80%, not on the next
 * poll. Only people with a limit need it.
 */
export function useRefreshAfterTurn(running: boolean) {
  const billing = useBilling();
  const hasLimit = Boolean(billing?.status?.enabled);
  const refresh = billing?.refresh;
  const wasRunning = useRef(running);
  useEffect(() => {
    const settled = wasRunning.current && !running;
    wasRunning.current = running;
    if (settled && hasLimit) void refresh?.();
  }, [running, hasLimit, refresh]);
}

export function MemberLimitNotice({ running = false }: { running?: boolean }) {
  useRefreshAfterTurn(running);
  const status = useBilling()?.status;
  const copy = useNamespaceCopy(billingCopy);
  const operations = useNamespaceCopy(operationCopy);
  const locale = useIntlLocale();
  const values = limitNoticeValues(status);
  const companyBalance = companyCreditNotice(status);
  if (!values && companyBalance === null) return null;
  if (!values)
    return (
      <p className="limit-notice" role="status" data-testid="company-credit-notice">
        {formatCopy(operations.lowBalance, {
          credits: credits(companyBalance! / CREDIT_MICROS, locale),
        })}{' '}
        <a href="#/settings/company/usage">{copy.seeUsage}</a>
      </p>
    );
  const left = credits(values.leftMicros / CREDIT_MICROS, locale);
  const limit = credits(values.limitMicros / CREDIT_MICROS, locale);
  return (
    <p className="limit-notice" role="status" data-testid="member-limit-notice">
      {values.resetsAt
        ? formatCopy(copy.limitNotice, { left, limit, date: shortDate(values.resetsAt, locale) })
        : formatCopy(copy.limitNoticeNoDate, { left, limit })}
    </p>
  );
}
